// /support/new — open a new ticket.

import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import type { SupportCategoryLite, SupportTicketSummary } from '../types';

export function SupportNewPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<SupportCategoryLite[]>([]);
  const [categoryId, setCategoryId] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const list = await api<SupportCategoryLite[]>('/api/support/categories');
        setCategories(list);
        if (list[0]) setCategoryId(list[0].id);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load categories');
      }
    })();
  }, []);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const t = await api<SupportTicketSummary>('/api/support/tickets', {
        method: 'POST',
        body: JSON.stringify({ subject, message, categoryId }),
      });
      navigate(`/support/${t.ticketNumber}`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open ticket');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page-content">
      <header className="dashboard-header">
        <h1>New ticket</h1>
        <p>We'll email you when there's an update.</p>
      </header>

      {error && <div className="form-error">{error}</div>}

      <form onSubmit={(e) => void onSubmit(e)} className="admin-form" style={{ maxWidth: 640 }}>
        <div className="form-group">
          <label>Category</label>
          <select className="admin-select" required value={categoryId} onChange={(e) => setCategoryId(e.target.value)} disabled={submitting}>
            <option value="">— select —</option>
            {categories.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
          </select>
        </div>
        <div className="form-group">
          <label>Subject</label>
          <input type="text" required value={subject} onChange={(e) => setSubject(e.target.value)} disabled={submitting} maxLength={200} />
        </div>
        <div className="form-group">
          <label>What's going on?</label>
          <textarea
            className="admin-textarea"
            rows={8}
            required
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={submitting}
            placeholder="Describe the issue, what you tried, and the result."
          />
        </div>
        <div className="form-actions">
          <Link to="/support" className="btn-secondary">Cancel</Link>
          <button type="submit" className="btn-primary" disabled={submitting || !categoryId}>
            {submitting ? 'Opening…' : 'Open ticket'}
          </button>
        </div>
      </form>
    </div>
  );
}
